import { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import { adminApi } from '../../services/api';

interface Holiday {
  date: string;
  name: string;
  type: 'PUBLIC' | 'SPECIAL';
}

export default function HolidayManagementPage() {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Form state for adding new holiday
  const [showAddForm, setShowAddForm] = useState(false);
  const [newHoliday, setNewHoliday] = useState<{ date: string; name: string; type: 'PUBLIC' | 'SPECIAL' }>({ date: '', name: '', type: 'PUBLIC' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generate year state
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
  const [generateYear, setGenerateYear] = useState(currentYear + 1);
  const [isGenerating, setIsGenerating] = useState(false);

  // Delete confirmation
  const [deleteDate, setDeleteDate] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchHolidays = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await adminApi.listHolidays(selectedYear.toString());
      const data = response as { holidays: Holiday[] };
      setHolidays(data.holidays || []);
    } catch (err: any) {
      console.error('Error fetching holidays:', err);
      setError(err.message || 'Failed to load holidays');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, [selectedYear]);

  const handleAddHoliday = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      await adminApi.createHoliday(newHoliday);
      setSuccessMessage(`Holiday "${newHoliday.name}" added successfully`);
      setNewHoliday({ date: '', name: '', type: 'PUBLIC' });
      setShowAddForm(false);
      fetchHolidays();
    } catch (err: any) {
      setError(err.message || 'Failed to add holiday');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteHoliday = async () => {
    if (!deleteDate) return;

    setIsDeleting(true);
    setError('');
    setSuccessMessage('');

    try {
      await adminApi.deleteHoliday(deleteDate);
      setSuccessMessage('Holiday deleted successfully');
      setDeleteDate(null);
      fetchHolidays();
    } catch (err: any) {
      setError(err.message || 'Failed to delete holiday');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGenerateHolidays = async () => {
    setIsGenerating(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await adminApi.generateHolidays({ year: generateYear, overwrite: false });
      const data = response as { count: number; message: string };
      setSuccessMessage(data.message || `Generated holidays for ${generateYear}`);
      setShowGenerateConfirm(false);
      setSelectedYear(generateYear);
    } catch (err: any) {
      if (err.message?.includes('already exist')) {
        setError(`Holidays already exist for ${generateYear}. Delete them first or use a different year.`);
      } else {
        setError(err.message || 'Failed to generate holidays');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const yearOptions = [];
  for (let y = currentYear - 2; y <= currentYear + 5; y++) {
    yearOptions.push(y);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Holiday Management</h1>
          <p className="text-sm text-gray-600">Manage public holidays for the leave calendar</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowGenerateConfirm(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Generate Year
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Holiday
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="rounded-md bg-green-50 p-4">
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Year Selector */}
      <div className="card p-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Year:</label>
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(parseInt(e.target.value))}
            className="input w-32"
          >
            {yearOptions.map(y => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-500">
            {holidays.length} holiday{holidays.length !== 1 ? 's' : ''} found
          </span>
        </div>
      </div>

      {/* Holidays Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            <span className="ml-2 text-gray-600">Loading holidays...</span>
          </div>
        ) : holidays.length === 0 ? (
          <div className="py-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-gray-600">No holidays found for {selectedYear}</p>
            <button
              onClick={() => {
                setGenerateYear(selectedYear);
                setShowGenerateConfirm(true);
              }}
              className="mt-4 btn-primary"
            >
              Generate {selectedYear} Holidays
            </button>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Holiday
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Type
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {holidays.map(holiday => (
                <tr key={holiday.date} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{formatDate(holiday.date)}</div>
                    <div className="text-xs text-gray-500">{holiday.date}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{holiday.name}</div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                        holiday.type === 'SPECIAL'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {holiday.type}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right">
                    <button
                      onClick={() => setDeleteDate(holiday.date)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Holiday Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Add Holiday</h2>
            <form onSubmit={handleAddHoliday} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Date</label>
                <input
                  type="date"
                  required
                  value={newHoliday.date}
                  onChange={e => setNewHoliday({ ...newHoliday, date: e.target.value })}
                  className="input mt-1 w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Holiday Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g., Election Day"
                  value={newHoliday.name}
                  onChange={e => setNewHoliday({ ...newHoliday, name: e.target.value })}
                  className="input mt-1 w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <select
                  value={newHoliday.type}
                  onChange={e => setNewHoliday({ ...newHoliday, type: e.target.value as 'PUBLIC' | 'SPECIAL' })}
                  className="input mt-1 w-full"
                >
                  <option value="PUBLIC">Public Holiday</option>
                  <option value="SPECIAL">Special (One-off)</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className="btn-primary">
                  {isSubmitting ? 'Adding...' : 'Add Holiday'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Year Modal */}
      {showGenerateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Generate Holidays</h2>
            <p className="mb-4 text-sm text-gray-600">
              This will generate all standard South African public holidays for the selected year,
              including calculated dates for Easter-based holidays (Good Friday, Family Day).
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Year to Generate</label>
              <select
                value={generateYear}
                onChange={e => setGenerateYear(parseInt(e.target.value))}
                className="input mt-1 w-full"
              >
                {yearOptions.map(y => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowGenerateConfirm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateHolidays}
                disabled={isGenerating}
                className="btn-primary"
              >
                {isGenerating ? 'Generating...' : 'Generate Holidays'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Delete Holiday</h2>
            <p className="mb-4 text-sm text-gray-600">
              Are you sure you want to delete the holiday on {deleteDate}? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteDate(null)} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleDeleteHoliday}
                disabled={isDeleting}
                className="bg-red-600 px-4 py-2 text-white rounded-lg hover:bg-red-700"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
